import React, { useState, useCallback, useRef, useMemo } from 'react';
import tw, { styled, css } from 'twin.macro';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd';
import {
  Check,
  Clock,
  CircleNotch,
  Crop as CropIcon,
  DotsSixVertical,
  Image,
  Pause,
  Play,
  Plus,
  Sparkle,
  SlidersHorizontal,
  Trash,
  VideoCamera,
  ArrowLeft,
  ArrowRight,
} from '@phosphor-icons/react';
import TemplateStylePicker from './TemplateStylePicker';
import { withoutScene } from './storedCrops';
import { VideoScene, VideoTemplateStyle } from './types';
import { pictureSwapUpdates } from './slideEdits';
import { applyStylePace, StylePaces } from './stylePace';
import {
  MAX_SLIDES,
  canAddSlide,
  newSceneFromResource,
  newSlideDuration,
  nextFreeIndex,
} from './addSlide';
import {
  maxLengthForField,
  slideBudgetMessage,
  slideCharacterBudget,
} from './slideText';
import {
  backToLook,
  DEFAULT_LOGO_POSITION,
  handSetNote,
  logoIsSetByHand,
  logoMarker,
  LogoPosition,
  sceneIsSetByHand,
  shownTransition,
  transitionClickIsAChoice,
} from './advancedScenes';
import { shownDuration } from './shownDuration';
import { StalePreviewNote } from './StalePreviewNote';
import { LogoPlaceButtons } from 'components/logo-place/LogoPlaceButtons';
import { detectScenesEdited, VideoWideChoices } from './updatePayload';
import { StyleExamplesState } from './styleExamples';
import { sceneRowMeta } from './sceneMeta';
import { TRANSITION_OPTIONS } from './mockData';
import { useCreatomatePreview } from 'hooks/useCreatomatePreview';
import { useTrack1Preview } from 'hooks/useTrack1Preview';
import { PreviewPlan } from 'api/video';
import { Track1PreviewFrame } from './Track1PreviewFrame';
import {
  PREVIEW_MAX_WIDTH,
  previewBoxWidth,
  previewShape,
  REVIEW_LIST_PANE_WIDTH,
} from './wizardLayout';
import { planAspect } from './previewTimeline';
import { choosePlayer, paneError as paneErrorFor } from './choosePlayer';
import Button from '../button/Button';
import ResourcesModal from 'components/resources-modal/ResourcesModal';
import AdEditorImageCropper from 'components/ad-builder/ad-editor-cropper/Cropper';
import { Crop } from 'react-image-crop';
import { CustomerResource } from 'types/apiTypes';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type WizardStep = 1 | 2 | 3 | 4;

/**
 * SYM-6308 (Steven, 22 Sep 2026). The step the wizard opens on.
 *
 * It lives here, beside the steps themselves, because it is a statement about
 * them rather than about the modal that happens to open the wizard: the video
 * is now on the Preview step and nowhere else, so that is where a customer has
 * to arrive, and Style and Scenes become where you go to change what you see.
 * SYM-6220 opened on Scenes for the opposite reason - the video was beside
 * every step anyway, so it made sense to land where the work was.
 */
export const WIZARD_OPENS_ON: WizardStep = 3;

/**
 * SYM-6342. How long a render usually takes, said while it runs.
 *
 * 25-45 s is what 9 renders in 10 took on QA (SYM-6340, 69 renders, 90% within
 * 43 s). It used to live only on step 4, which the wizard never opens (it opens
 * on Preview, WIZARD_OPENS_ON, and renders there under the overlay), so no
 * customer ever saw it; one text, used wherever a render is waited for.
 */
export const RENDER_WAIT_NOTE = 'This usually takes 25-45 seconds.';

interface VideoWizardProps {
  initialScenes: VideoScene[];
  initialStyle: VideoTemplateStyle;
  /**
   * Full Creatomate composition returned by /preview-config. Passed directly
   * to the Preview SDK — the FE does not build its own composition.
   */
  previewPayload: Record<string, any> | null;
  /**
   * SYM-6206. The plan for a video OUR renderer will make: the same pictures
   * ffmpeg composites, with their positions and timings.
   *
   * Present instead of a Creatomate composition for a draft on our renderer.
   * When it is here the pane plays it; when it is not, nothing changes.
   */
  previewPlan?: PreviewPlan | null;
  /**
   * SYM-6265. Where the draft says the logo sits. One choice for the whole
   * video, offered under Advanced; left out means top right.
   */
  initialLogoPosition?: LogoPosition;
  onRender: (
    scenes: VideoScene[],
    style: VideoTemplateStyle,
    choices?: VideoWideChoices
  ) => void;
  /**
   * Called when the user navigates to the Preview step after editing. Parent
   * is expected to call /update with the current state and update
   * `previewPayload` with the fresh composition so the SDK re-renders.
   */
  onRefreshPreview?: (
    scenes: VideoScene[],
    style: VideoTemplateStyle,
    choices?: VideoWideChoices
  ) => Promise<void>;
  /** True while /update is in flight from `onRefreshPreview`. */
  isRefreshingPreview?: boolean;
  onCancel: () => void;
  vacancyId?: number | null;
  /** Land directly on this step (default: 1). Pass 3 for backend-driven flow. */
  initialStep?: WizardStep;
  /** draft_id from /preview-config — passed through for parent tracking */
  draftId?: number;
  /**
   * SYM-6196. The most characters one slide's text may run to, as the server
   * said when it handed over the draft. Left out, the wizard falls back to the
   * server's own default; see slideText.ts.
   */
  maxSlideCharacters?: number | null;
  /**
   * SYM-6215. The pace each style stands for, as the server said. Picking a
   * style sets every slide the customer has not touched to its length. Left
   * out, picking a style leaves the lengths alone; see stylePace.ts.
   */
  stylePaces?: StylePaces | null;
  /** True while /generate is being polled */
  isRendering?: boolean;
  /** 0–100 render progress for the overlay progress bar */
  renderProgress?: number;
  /**
   * SYM-6226. The three moving examples the Style step shows, one per look, on
   * this draft's own picture, colour and logo.
   *
   * Asked for by the provider rather than here, once per draft, because the
   * Style step is mounted and thrown away every time the customer visits it and
   * the answer does not change while the wizard is open. See useStyleExamples.
   */
  styleExamples?: StyleExamplesState;
  /** Asks for them again after a failure. */
  onRetryStyleExamples?: () => void;
}

// ---------------------------------------------------------------------------
// Styled components — layout
// ---------------------------------------------------------------------------

/**
 * SYM-6288 (20 Sep 2026). A column that fits the pop-up: steps on top, buttons
 * at the bottom, and the middle takes what is left.
 *
 * The pop-up now has a height of its own (see wizardLayout.ts), so `h-full`
 * means something. The two bars refuse to shrink (`flex-shrink-0`) and the
 * middle is allowed to become smaller than its content (`min-h-0`; a flex item
 * otherwise never shrinks below what is inside it, which is how the button row
 * used to be pushed off screen).
 */
const Wrapper = tw.div`flex flex-col h-full min-h-0`;

const StepIndicatorBar = tw.div`flex flex-shrink-0 items-center gap-1 px-6 py-3 border-b border-gray-100 bg-white`;

const StepPill = styled.button<{ isActive: boolean; isCompleted: boolean }>`
  ${tw`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all`}
  ${({ isActive }) => isActive && tw`bg-indigo-50 text-indigo-700`}
  ${({ isCompleted, isActive }) =>
    isCompleted && !isActive && tw`text-green-600 hover:bg-green-50`}
  ${({ isActive, isCompleted }) =>
    !isActive && !isCompleted && tw`text-gray-400 cursor-default`}
`;

const StepNumber = styled.span<{ isActive: boolean; isCompleted: boolean }>`
  ${tw`flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold`}
  ${({ isActive }) => isActive && tw`bg-indigo-500 text-white`}
  ${({ isCompleted, isActive }) =>
    isCompleted && !isActive && tw`bg-green-500 text-white`}
  ${({ isActive, isCompleted }) =>
    !isActive && !isCompleted && tw`bg-gray-200 text-gray-500`}
`;

const StepConnector = tw.div`w-6 h-px bg-gray-200`;

const SplitLayout = tw.div`flex flex-1 min-h-0 overflow-hidden`;

/**
 * SYM-6308. The pane the video plays in takes whatever the pane beside it
 * leaves, rather than a width of its own.
 *
 * It was `w-[60%]`, which only added up on the two steps that put a 40% pane
 * beside it. It is now on two steps with different neighbours - the Preview
 * step's slide list and the render summary - so the neighbour states its width
 * and this one fills the rest. `min-w-0` is what lets it be squeezed rather
 * than overflow the pop-up.
 */
const PreviewPane = tw.div`flex flex-col items-center justify-center flex-1 min-w-0 min-h-0 bg-gray-50 p-6 overflow-hidden`;

const ControlPane = tw.div`w-[40%] bg-white border-l border-gray-200 overflow-y-auto p-5`;

/**
 * SYM-6308. The Style step, with no video beside it any more, is the whole
 * width of the pop-up.
 */
const StylePane = tw.div`flex-1 min-w-0 bg-white overflow-y-auto p-5`;

const FullWidthContent = tw.div`flex-1 flex flex-col items-center justify-center p-8 overflow-y-auto`;

const SceneListPane = tw.div`w-[35%] bg-gray-50 overflow-y-auto p-5 flex flex-col`;

/**
 * SYM-6308. The same list pane on the Preview step, where it is wider.
 *
 * It carries the slide list AND the message saying where to go to change any of
 * it, so it takes what the video used to have (45% against 35%). The width is
 * the one number the pop-up's own width was derived from, so the two cannot
 * drift apart; see wizardLayout.ts.
 *
 * It scrolls on its own, which is what keeps the new message from pushing the
 * video anywhere: a longer list makes this pane scroll, never the pop-up grow.
 */
const ReviewListPane = styled.div(() => [
  tw`bg-gray-50 overflow-y-auto p-5 flex flex-col`,
  { width: REVIEW_LIST_PANE_WIDTH },
]);

// SYM-6288. Scrolls on its own, like the slide list beside it: with Advanced
// open the editor grows a transition row and a length slider, and on a short
// window that has to scroll here rather than stretch the pop-up.
const SceneEditorPane = tw.div`w-[65%] bg-white border-l border-gray-200 p-5 flex flex-col overflow-y-auto`;

const EditorTopRow = tw.div`flex gap-5 items-stretch flex-1`;

const EditorTextColumn = tw.div`w-1/2 flex flex-col justify-end gap-3`;

const EditorImageColumn = tw.div`w-1/2 flex flex-col items-end`;

const EditorImageRow = tw.div`flex gap-3 items-end`;

/**
 * SYM-6179. The picture is shown in the shape the video will be, never square.
 *
 * A Track 1 video is delivered 1080x1350 for feed and 1080x1920 for story and
 * reels, and the picture inside this box is drawn with object-cover. In a square
 * box that means the part of the photograph on screen is not the part the video
 * keeps, so the customer chooses and crops against a frame that does not exist,
 * and only finds out after a render that cannot be edited again.
 *
 * The height stays fixed and the width follows the ratio, so the editor does not
 * jump about when the shape changes.
 */
const EditorImagePreview = styled.div<{ $ratio: number }>(({ $ratio }) => [
  tw`relative h-40 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0`,
  { aspectRatio: String($ratio), width: 'auto' },
]);

const EditorImageActions = tw.div`flex flex-col gap-1.5`;

const EditorBottomRow = tw.div`flex gap-6 mt-4 pt-3 border-t border-gray-100`;

const SectionHeading = tw.h3`text-sm font-semibold text-gray-700 uppercase tracking-wide`;

// SYM-6308. A second, wider preview pane used to be declared here (w-[65%]).
// Nothing rendered it - the wizard has only ever used PreviewPane - so it was a
// width that looked like a decision and was not one. Removed rather than
// rebalanced.

const SceneActions = tw.div`flex items-center gap-1 ml-auto`;

const TrashButton = styled.button`
  ${tw`p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition`}
`;

const BottomBar = tw.div`flex flex-shrink-0 items-center justify-between px-6 py-3 border-t border-gray-100 bg-gray-50`;

// ---------------------------------------------------------------------------
// Preview pane components
// ---------------------------------------------------------------------------

/**
 * SYM-6206 adds `ratio`: the video's own shape, as a number.
 *
 * The pane was a square while the video is 9:16 or 4:5, so a preview drawn in it
 * could never be what the customer gets. A plan states the canvas it was drawn
 * at, so when there is one the pane takes that shape instead of guessing.
 */
const PreviewContainer = styled.div<{ aspectRatio: string; ratio?: number }>`
  ${tw`relative flex-shrink-0 bg-gray-900 rounded-lg overflow-hidden`}
  ${({ aspectRatio, ratio }) => {
    // SYM-6288. Same shapes as before; the WIDTH is new. It was "full width, at
    // most 384px", which made a 9:16 box 683px tall whatever the window. Now it
    // is the largest box of this shape that fits the slot it sits in.
    const shape = previewShape(ratio, aspectRatio);

    // Two width lines on purpose. A browser that does not know container
    // units (Safari before 16, still inside our browser targets) drops the
    // second line as invalid and keeps the first, which is the width this box
    // always had. Without the first line the box would have no width at all
    // there and the preview would not be drawn (gate review, 21 Sep 2026).
    return css`
      aspect-ratio: ${shape};
      margin: auto;
      width: 100%;
      max-width: ${PREVIEW_MAX_WIDTH};
      width: ${previewBoxWidth(shape)};
    `;
  }}
`;

/**
 * SYM-6288. The room the preview box is fitted into: all the height the pane has
 * left once the play button, the note and the progress bar have taken theirs.
 *
 * `container-type: size` is what lets the box inside measure this slot (the
 * `cqw` / `cqh` units in wizardLayout.ts). The price is that the slot no longer
 * takes any size from its content, which is fine here and ONLY here because the
 * pop-up has a height of its own: in a pop-up sized by its content this slot
 * would collapse to nothing.
 */
const PreviewSlot = styled.div`
  /* The slot SCROLLS, and the box inside is centred with margin:auto rather than
     with align-items:center. Where the box is fitted to the slot (every browser
     that knows container units) nothing overflows and none of this is seen. In
     a browser that does not (Safari before 16) the box keeps its old size, 384
     by 683 for a story, which is taller than the slot on a laptop: centred the
     flex way its top would be cut off and out of reach for good; this way it
     starts at the top and the slot scrolls to the rest, as the whole pop-up
     used to (gate review, 21 Sep 2026; not reproduced in such a browser). */
  ${tw`flex flex-1 w-full min-h-0 overflow-y-auto`}
  container-type: size;
`;

const PreviewPlayerEl = tw.div`absolute inset-0`;

const PreviewPlaceholder = tw.div`absolute inset-0 flex flex-col items-center justify-center gap-3 text-gray-400`;

const PreviewPlaceholderText = tw.span`text-sm text-center px-4`;

const PreviewVideo = tw.video`absolute inset-0 w-full h-full object-contain bg-black`;

const PreviewControls = tw.div`flex flex-shrink-0 items-center justify-center gap-3 mt-3`;

const PlayPauseButton = styled.button`
  ${tw`flex items-center justify-center w-10 h-10 rounded-full bg-indigo-500 hover:bg-indigo-600 text-white transition`}
`;

const PreviewTime = tw.span`text-xs text-gray-500 font-mono`;

const GeneratePreviewButton = styled.button`
  ${tw`flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-medium rounded-md transition mt-2`}
`;

// Scene thumbnail shown in the preview pane when editing a specific scene
const ScenePreviewImage = tw.img`w-full h-full object-cover`;

// ---------------------------------------------------------------------------
// Step 1: Style picker — bigger cards
// ---------------------------------------------------------------------------

const StylePickerHeading = tw.h3`text-lg font-semibold text-gray-800 mb-1`;
const StylePickerSubtext = tw.p`text-sm text-gray-500 mb-5`;

// ---------------------------------------------------------------------------
// Step 2: Vertical scene list
// ---------------------------------------------------------------------------

const SceneListContainer = tw.div`flex flex-col gap-0`;

const SceneRow = styled.div<{ isExpanded: boolean; isDragging: boolean }>`
  ${tw`border border-gray-200 rounded-lg bg-white overflow-hidden transition-all`}
  ${({ isExpanded }) =>
    isExpanded && tw`ring-2 ring-indigo-500 border-transparent`}
  ${({ isDragging }) => isDragging && tw`shadow-xl opacity-90`}
  & + & {
    ${tw`mt-2`}
  }
`;

const SceneRowHeader = tw.div`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition`;

const DragHandle = tw.div`flex items-center text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing`;

/** SYM-6179. Same reasoning as EditorImagePreview: the thumbnail carries the
 *  video's shape, so a row of slides reads as the video rather than as squares. */
const SceneThumb = styled.img<{ $ratio: number }>(({ $ratio }) => [
  tw`h-12 rounded object-cover bg-gray-100 flex-shrink-0`,
  { aspectRatio: String($ratio), width: 'auto' },
]);

const SceneInfo = tw.div`flex-1 min-w-0`;

const SceneHeadline = tw.p`text-sm font-medium text-gray-800 truncate`;

const SceneMeta = tw.div`flex items-center gap-2 mt-0.5`;

const SceneRowBody = tw.div`px-3 pb-4 border-t border-gray-100`;

// ---------------------------------------------------------------------------
// Inline scene editor fields (reusing SceneEditor patterns)
// ---------------------------------------------------------------------------

const FieldSection = tw.div`mt-4`;
const FieldLabel = tw.label`block text-xs font-medium text-gray-500 mb-1.5`;

const Textarea = styled.textarea`
  ${tw`w-full border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
`;

const SubtextInput = styled.input`
  ${tw`w-full border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
`;

/** SYM-6196. How much of a slide's text budget is left, under the two boxes. */
const TextBudget = styled.p<{ $spent: boolean }>(({ $spent }) => [
  tw`mt-3 pt-2 border-t border-gray-100 text-xs`,
  $spent ? tw`text-amber-600` : tw`text-gray-400`,
]);

const InlineImagePreview = tw.div`relative rounded-lg overflow-hidden bg-gray-100 aspect-video mb-2 isolate`;

const CropBadge = tw.span`absolute bottom-2 left-2 bg-indigo-500 text-white text-[10px] font-semibold px-2 py-0.5 rounded z-10`;

const ImageActionButton = styled.button`
  ${tw`flex items-center justify-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-md text-xs text-gray-600 hover:bg-gray-50 transition`}
`;

const ImageButtonRow = tw.div`flex gap-2`;

const TransitionGrid = tw.div`grid grid-cols-4 gap-1.5`;

const TransitionOption = styled.button<{ isSelected: boolean }>`
  ${tw`px-2 py-1.5 rounded border text-xs font-medium transition`}
  ${({ isSelected }) =>
    isSelected
      ? tw`border-indigo-500 bg-indigo-50 text-indigo-700`
      : tw`border-gray-200 text-gray-500 hover:bg-gray-50`}
`;

const DurationRow = tw.div`flex items-center gap-2`;

const DurationSlider = styled.input`
  ${tw`flex-1 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer`}
  &::-webkit-slider-thumb {
    ${tw`appearance-none w-3.5 h-3.5 bg-indigo-500 rounded-full cursor-pointer`}
  }
`;

const DurationValue = tw.span`text-xs font-medium text-gray-600 w-6 text-right`;

// SYM-6265. The simple Scenes tab and its one Advanced switch.
const HandSetMarker = tw.button`mt-3 inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 cursor-pointer`;
const BackToLook = tw.div`mt-3 flex items-center gap-2 text-xs text-gray-500`;
const BackToLookLink = tw.button`text-indigo-600 font-medium hover:underline cursor-pointer bg-transparent border-0 p-0`;
const AdvancedSwitch = tw.button`inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-indigo-600 cursor-pointer bg-transparent border-0 p-0`;
const AdvancedPanel = tw.div`mb-3 rounded-md border border-gray-200 bg-white p-3`;
const RowHandDot = tw.span`ml-1 inline-block h-1.5 w-1.5 rounded-full bg-amber-500 align-middle`;

// ---------------------------------------------------------------------------
// Step 3: Summary
// ---------------------------------------------------------------------------

/**
 * SYM-6308. Under the slide list: where to go to change any of it.
 *
 * The Preview step is now where the wizard opens, so it is the first thing a
 * customer sees and it has no controls of its own. The two step names in it are
 * the controls that take you to the step that does.
 */
const WhereToChange = tw.p`mt-4 pt-4 border-t border-gray-200 text-xs leading-5 text-gray-500`;

const StepLink = tw.button`text-indigo-600 font-medium hover:underline cursor-pointer bg-transparent border-0 p-0`;

const SummaryCard = tw.div`bg-gray-50 rounded-lg p-5 mb-4`;
const SummaryRow = tw.div`flex justify-between items-center py-2 border-b border-gray-100 last:border-0`;
const SummaryLabel = tw.span`text-sm text-gray-500`;
const SummaryValue = tw.span`text-sm font-medium text-gray-800`;

// ---------------------------------------------------------------------------
// Step names
// ---------------------------------------------------------------------------

const STEP_LABELS: Record<WizardStep, string> = {
  1: 'Style',
  2: 'Scenes',
  3: 'Preview',
  4: 'Render',
};

// ---------------------------------------------------------------------------
// Inline Scene Editor (collapsible row body)
// ---------------------------------------------------------------------------

interface InlineSceneEditorProps {
  scene: VideoScene;
  onUpdate: (sceneId: string, updates: Partial<VideoScene>) => void;
  vacancyId?: number | null;
  /** Aspect ratio the cropper must constrain to — matches the video's output
   * width/height so what the user crops equals what the final video shows. */
  cropAspect?: number;
  /**
   * SYM-6201. True for the slide that opens the video. The transition belongs
   * to the slide it brings ON, so the first one has nothing to come from and
   * the server throws its setting away: `CreatomatePayloadMapper.php:587-589`
   * resolves slide 0 to no transition, and `:170` only attaches the animation
   * when the slide is not the first.
   */
  isFirstSlide?: boolean;
  /**
   * SYM-6265. True when the Advanced switch is on: only then are the
   * transition buttons and the length slider shown.
   */
  advanced?: boolean;
  /** The transition to show as chosen: the slide's own when set by hand, else the look's. */
  transitionShown?: VideoScene['transition'];
  /** Says which of this slide's values were set by hand; null when none. */
  handNote?: string | null;
  /** Turns the Advanced switch on (the marker on the simple tab calls this). */
  onOpenAdvanced?: () => void;
  /** Hands this slide back to its look. */
  onBackToLook?: () => void;
  /**
   * SYM-6196. The most characters a slide's two lines may hold between them,
   * as the server said. See slideText.ts.
   */
  characterBudget: number;
}

const InlineSceneEditor: React.FC<InlineSceneEditorProps> = ({
  scene,
  onUpdate,
  vacancyId,
  cropAspect = 9 / 16,
  isFirstSlide = false,
  advanced = false,
  transitionShown,
  handNote = null,
  onOpenAdvanced,
  onBackToLook,
  characterBudget,
}) => {
  // SYM-6207. A slide can arrive longer than the budget, because the model is
  // asked to keep to it and never checked against it. Say so, rather than let
  // the customer find out by being refused a keystroke.
  const budget = slideBudgetMessage(
    scene.headline,
    scene.subtext,
    characterBudget
  );
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [cropData, setCropData] = useState<Crop | null>(
    scene.imageCrop ? (scene.imageCrop as Crop) : null
  );
  const [croppedPreviewUrl, setCroppedPreviewUrl] = useState<string | null>(
    null
  );

  const handleImageSelect = (resources: CustomerResource[]) => {
    if (resources.length > 0) {
      // SYM-6195. The cut-out copy of the old photograph goes with the crop
      // rectangle; see slideEdits.ts for why leaving it behind is fatal.
      onUpdate(scene.id, pictureSwapUpdates(resources[0]));
      setCropData(null);
      setCroppedPreviewUrl(null);
      setShowCropper(false);
    }
    setShowImagePicker(false);
  };

  const handleCropComplete = (crop: Crop | null) => {
    setCropData(crop);
  };

  const handleDoneCropping = () => {
    onUpdate(scene.id, { imageCrop: cropData });
    if (cropData && cropData.unit === '%') {
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const sx = (cropData.x / 100) * img.naturalWidth;
        const sy = (cropData.y / 100) * img.naturalHeight;
        const sw = (cropData.width / 100) * img.naturalWidth;
        const sh = (cropData.height / 100) * img.naturalHeight;
        canvas.width = sw;
        canvas.height = sh;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
          try {
            const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
            setCroppedPreviewUrl(dataUrl);
            onUpdate(scene.id, {
              imageCrop: cropData,
              croppedPreviewUrl: dataUrl,
            });
            return;
          } catch {
            setCroppedPreviewUrl(null);
          }
        }
      };
      img.src = scene.imageUrl;
    } else {
      setCroppedPreviewUrl(null);
    }
    setShowCropper(false);
  };

  return (
    <>
      <SceneRowBody>
        {/* TOP ROW: Text fields LEFT (50%) | Image RIGHT (50%), bottom-aligned */}
        <EditorTopRow>
          <EditorTextColumn>
            <FieldSection>
              <FieldLabel>Headline</FieldLabel>
              <Textarea
                rows={2}
                value={scene.headline}
                maxLength={maxLengthForField(
                  scene.headline,
                  scene.subtext,
                  characterBudget
                )}
                onChange={(e) =>
                  onUpdate(scene.id, { headline: e.target.value })
                }
                placeholder="Enter headline text..."
              />
            </FieldSection>
            <FieldSection>
              <FieldLabel>Subtext</FieldLabel>
              <SubtextInput
                value={scene.subtext || ''}
                maxLength={maxLengthForField(
                  scene.subtext,
                  scene.headline,
                  characterBudget
                )}
                onChange={(e) =>
                  onUpdate(scene.id, { subtext: e.target.value })
                }
                placeholder="Optional supporting text..."
              />
            </FieldSection>

            {/*
              SYM-6196. The budget is for the slide, not for either line, so it
              is counted once and sits under BOTH boxes rather than under the
              second one, where it read as if it belonged to that box alone.
            */}
            <TextBudget $spent={budget.state !== 'within'}>
              {budget.text}
            </TextBudget>
          </EditorTextColumn>

          <EditorImageColumn>
            {showCropper ? (
              <>
                <AdEditorImageCropper
                  uuid={scene.id}
                  image={scene.imageUrl}
                  crop={cropData}
                  aspect={cropAspect}
                  autoFitAspectOnMount
                  onCropComplete={handleCropComplete}
                />
                <ImageActionButton onClick={handleDoneCropping} tw="mt-2">
                  Done cropping
                </ImageActionButton>
              </>
            ) : (
              <EditorImageRow>
                <EditorImagePreview $ratio={cropAspect}>
                  <ScenePreviewImage
                    src={
                      croppedPreviewUrl ||
                      scene.croppedPreviewUrl ||
                      scene.imageUrl
                    }
                    alt="Scene preview"
                  />
                  {(croppedPreviewUrl || scene.croppedPreviewUrl) && (
                    <CropBadge>Cropped</CropBadge>
                  )}
                </EditorImagePreview>
                <EditorImageActions>
                  <ImageActionButton onClick={() => setShowCropper(true)}>
                    <CropIcon size={14} />
                    Crop
                  </ImageActionButton>
                  <ImageActionButton onClick={() => setShowImagePicker(true)}>
                    <Image size={14} />
                    Change
                  </ImageActionButton>
                </EditorImageActions>
              </EditorImageRow>
            )}
          </EditorImageColumn>
        </EditorTopRow>

        {/*
          SYM-6265. The look decides how long a slide lasts and how it changes
          to the next, so the simple tab does not ask. What is in force is never
          hidden though: a slide whose values were set by hand says so, and
          saying so is the way in.
        */}
        {!advanced && handNote && (
          <HandSetMarker type="button" onClick={onOpenAdvanced}>
            <SlidersHorizontal size={13} />
            {handNote}
          </HandSetMarker>
        )}

        {/* BOTTOM ROW: Transition + Duration side by side, under Advanced */}
        {advanced && (
          <EditorBottomRow>
            {/*
              SYM-6201. The setting governs the change INTO this slide, which
              is what the rendered video does and the opposite of how
              "Transition" alone reads. The first slide is not offered it at
              all, because the server discards it.
            */}
            {!isFirstSlide && (
              <FieldSection tw="flex-1">
                <FieldLabel>Transition into this slide</FieldLabel>
                <TransitionGrid>
                  {TRANSITION_OPTIONS.map((opt) => (
                    <TransitionOption
                      key={opt.value}
                      isSelected={
                        (transitionShown ?? scene.transition) === opt.value
                      }
                      onClick={() =>
                        // SYM-6265. Chosen by hand: from now on the look's
                        // order leaves this slide's transition alone. Pressing
                        // the one the look already gives is not a choice: it
                        // would swap the look's direction for plain "slide".
                        transitionClickIsAChoice(
                          scene,
                          transitionShown,
                          opt.value
                        ) &&
                        onUpdate(scene.id, {
                          transition: opt.value,
                          transitionSetByHand: true,
                          transitionAsStored: undefined,
                        })
                      }
                    >
                      {opt.label}
                    </TransitionOption>
                  ))}
                </TransitionGrid>
              </FieldSection>
            )}

            <FieldSection tw="flex-1">
              <FieldLabel>Duration</FieldLabel>
              <DurationRow>
                <Clock size={14} tw="text-gray-400" />
                <DurationSlider
                  type="range"
                  min={1}
                  max={6}
                  step={0.5}
                  value={scene.duration}
                  onChange={(e) =>
                    // SYM-6215. Moved by hand: from now on a style leaves this
                    // slide's length alone.
                    onUpdate(scene.id, {
                      duration: parseFloat(e.target.value),
                      durationSetByHand: true,
                    })
                  }
                />
                <DurationValue>{scene.duration}s</DurationValue>
              </DurationRow>
            </FieldSection>
          </EditorBottomRow>
        )}

        {advanced && handNote && (
          <BackToLook>
            <span>{handNote}.</span>
            <BackToLookLink type="button" onClick={onBackToLook}>
              Back to the style&apos;s setting
            </BackToLookLink>
          </BackToLook>
        )}
      </SceneRowBody>

      <ResourcesModal
        show={showImagePicker}
        mode="image"
        onClose={() => setShowImagePicker(false)}
        onSubmit={handleImageSelect}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const VideoWizard: React.FC<VideoWizardProps> = ({
  initialScenes,
  initialStyle,
  initialLogoPosition = DEFAULT_LOGO_POSITION,
  previewPayload,
  previewPlan,
  onRender,
  onRefreshPreview,
  isRefreshingPreview = false,
  onCancel,
  vacancyId,
  initialStep = 1,
  maxSlideCharacters,
  stylePaces,
  isRendering = false,
  renderProgress = 0,
  styleExamples = { status: 'loading' },
  onRetryStyleExamples,
}) => {
  // SYM-6196. One number for the whole wizard: what the server said, or its
  // own default when it said nothing.
  const characterBudget = slideCharacterBudget(maxSlideCharacters);
  const [step, setStep] = useState<WizardStep>(initialStep);
  const [scenes, setScenes] = useState<VideoScene[]>(initialScenes);
  // SYM-6265. One switch for the whole tab. A view and nothing more: it is not
  // stored, and turning it off hides the controls without resetting anything.
  const [advanced, setAdvanced] = useState(false);
  const [logoPosition, setLogoPosition] =
    useState<LogoPosition>(initialLogoPosition);
  const choices: VideoWideChoices = useMemo(
    () => ({ logoPosition }),
    [logoPosition]
  );
  const [templateStyle, setTemplateStyle] =
    useState<VideoTemplateStyle>(initialStyle);
  // SYM-6220. Opened past Style (initialStep 2 or, since SYM-6308, 3), the
  // first slide is selected as it would have been had the customer walked in
  // from Style (handleStyleSelect, handleGoToStep2 do the same). Opening on
  // Preview it is not shown anywhere, but it is what the Scenes step finds
  // waiting when the message under the slide list sends someone there.
  const [expandedSceneId, setExpandedSceneId] = useState<string | null>(
    initialStep >= 2 && initialScenes.length > 0 ? initialScenes[0].id : null
  );
  /** SYM-6194. Open while the picture for a slide being added is chosen. */
  const [showAddPicker, setShowAddPicker] = useState(false);

  // Preview player — driven entirely by BE-provided `previewPayload`.
  // The FE does not build its own Creatomate composition.
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const publicToken = (import.meta as any).env?.VITE_CREATOMATE_PUBLIC_TOKEN as
    | string
    | undefined;

  const {
    isReady: previewReady,
    isPlaying: previewPlaying,
    currentTime: previewTime,
    error: previewError,
    play: previewPlay,
    pause: previewPause,
  } = useCreatomatePreview(
    previewContainerRef,
    previewPayload,
    publicToken,
    step
  );

  /**
   * SYM-6206. Which player drives the pane.
   *
   * A draft our own renderer will make arrives with a plan, and the browser
   * plays the very pictures ffmpeg will composite. A Creatomate draft has no
   * plan and keeps the SDK player above, unchanged.
   */
  const planPlayer = useTrack1Preview(previewPlan);

  // The rule itself lives in choosePlayer, with its own tests: inside this JSX
  // it would be three conditions that are awkward to exercise -- and "the
  // preview pane went blank" reaches the customer. (SYM-6308 added component
  // tests for the wizard, but only for which step draws the pane at all; the
  // three states of the player are still proved next to the rule.)
  const pane = choosePlayer({
    hasPlan: !!previewPlan,
    hasCreatomateToken: !!publicToken,
    sdkError: previewError,
  });
  const playsOwnPlan = pane === 'plan';

  const playerReady = playsOwnPlan ? planPlayer.isReady : previewReady;
  const playerPlaying = playsOwnPlan ? planPlayer.isPlaying : previewPlaying;
  const playerTime = playsOwnPlan ? planPlayer.currentTime : previewTime;
  const playerPlay = playsOwnPlan ? planPlayer.play : previewPlay;
  const playerPause = playsOwnPlan ? planPlayer.pause : previewPause;

  // Our own player needs no Creatomate token, and must not be hidden by the
  // SDK's complaint about not having one.
  const hasPreview = pane !== 'placeholder';
  const paneError = paneErrorFor(!!previewPlan, previewError);

  // Constrain the image cropper to the video's output aspect ratio so what the
  // user crops is exactly what the final render shows. Creatomate delivers the
  // canvas dimensions in the preview_payload; fall back to 9:16 (the default
  // when the payload hasn't arrived yet).
  const cropAspect = useMemo(() => {
    // SYM-6206. Our own plan states the canvas too, and it is the one the video
    // is actually made at.
    if (previewPlan) return planAspect(previewPlan);

    const w = previewPayload?.width;
    const h = previewPayload?.height;
    return typeof w === 'number' && typeof h === 'number' && h > 0
      ? w / h
      : 9 / 16;
  }, [previewPayload, previewPlan]);
  // SYM-6199. Between an edit and the next rebuild the preview is the version
  // before that edit; the pane says so, and the length shown must not pretend
  // otherwise.
  const showsAnOlderVersion = detectScenesEdited(
    scenes,
    initialScenes,
    templateStyle,
    initialStyle,
    logoPosition,
    // The baseline is the context's, like the scenes' and the look's: it only
    // moves when the server really took the change, so a rebuild that failed
    // quietly leaves the "older version" note standing.
    initialLogoPosition
  );
  // SYM-6273. One length, shown the same way in the summary rows and under
  // the preview: the plan's when we have the plan of the video as it stands.
  const videoLength = shownDuration({
    sceneDurations: scenes.map((s) => s.duration),
    planDuration: playsOwnPlan ? previewPlan?.duration : null,
    planIsCurrent: !showsAnOlderVersion,
  });
  // The timer counts against whatever is actually playing, so it keeps the
  // length of the plan on screen even when an edit has made that plan old.
  const playingLength = shownDuration({
    sceneDurations: scenes.map((s) => s.duration),
    planDuration: playsOwnPlan ? previewPlan?.duration : null,
    planIsCurrent: true,
  });

  // Track which steps have been visited
  // Pre-mark all steps up to initialStep as visited so the user can navigate back
  //
  // SYM-6308. Opening on Preview (initialStep 3) this marks Style and Scenes
  // done, and that is the right reading rather than a side effect: the draft
  // arrives with a look already chosen and slides already written, so both
  // steps really have been done - by the server - and both are somewhere to go
  // back to. The step row's own rule already allowed moving back to any step at
  // or below the current one, so nothing here decides whether they are
  // reachable, only whether they are ticked.
  const [visitedSteps, setVisitedSteps] = useState<Set<WizardStep>>(
    new Set(([1, 2, 3, 4] as WizardStep[]).filter((s) => s <= initialStep))
  );

  const goToStep = useCallback((target: WizardStep) => {
    setStep(target);
    setVisitedSteps((prev) => new Set([...prev, target]));
  }, []);

  // ---------------------------------------------------------------------------
  // Scene handlers (same logic as VideoScenePreview)
  // ---------------------------------------------------------------------------

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;
      const reordered = Array.from(scenes);
      const [moved] = reordered.splice(result.source.index, 1);
      reordered.splice(result.destination.index, 0, moved);
      // Preserve each scene's `index` — it points to the original AI-generated
      // image slot the backend uses to resolve the source image. Reassigning
      // by position makes reorder a no-op on the /update payload.
      setScenes(reordered);
    },
    [scenes]
  );

  const handleRemoveScene = useCallback(
    (sceneId: string) => {
      // Keep remaining scenes' original `index` so the backend still resolves
      // each slide to the correct source image (see handleDragEnd).
      setScenes((prev) => withoutScene(prev, sceneId));
      if (expandedSceneId === sceneId) setExpandedSceneId(null);
    },
    [expandedSceneId]
  );

  const handleUpdateScene = useCallback(
    (sceneId: string, updates: Partial<VideoScene>) => {
      setScenes((prev) =>
        prev.map((s) => (s.id === sceneId ? { ...s, ...updates } : s))
      );
    },
    []
  );

  /**
   * SYM-6194. Adding a slide starts by choosing its picture.
   *
   * A slide with no picture of the customer's own cannot survive the trip to
   * the server, so there is nothing to add until one has been picked. See
   * addSlide.ts.
   */
  const handleAddScene = useCallback(() => {
    if (!canAddSlide(scenes.length)) return;
    setShowAddPicker(true);
  }, [scenes.length]);

  const handleAddScenePicked = useCallback(
    (resources: CustomerResource[]) => {
      setShowAddPicker(false);
      if (!resources.length) return;

      // SYM-6299. The new slide is born at the pace the chosen look stands
      // for, not at a flat 3 s, so it reads the same length as the slides
      // beside it and the total adds up to what the look promises.
      const newScene = newSceneFromResource(
        resources[0],
        nextFreeIndex(scenes),
        newSlideDuration(templateStyle, stylePaces)
      );
      setScenes((prev) => [...prev, newScene]);
      setExpandedSceneId(newScene.id);
    },
    [scenes, templateStyle, stylePaces]
  );

  const handleStyleSelect = useCallback(
    (style: VideoTemplateStyle) => {
      setTemplateStyle(style);
      // SYM-6215. The style's pace goes onto every slide the customer has not
      // set by hand, so the sliders and the total read the new lengths at
      // once and the next preview is built from them.
      setScenes((prev) => applyStylePace(prev, style, stylePaces));
      // Auto-advance to Step 2 after selecting style, select first scene
      setTimeout(() => {
        goToStep(2);
        if (scenes.length > 0) {
          setExpandedSceneId(scenes[0].id);
        }
      }, 300);
    },
    [goToStep, scenes, stylePaces]
  );

  // ---------------------------------------------------------------------------
  // Render handlers
  // ---------------------------------------------------------------------------

  const handleRenderComplete = useCallback(() => {
    setTimeout(() => onRender(scenes, templateStyle, choices), 500);
  }, [scenes, templateStyle, choices, onRender]);

  // The scene currently being edited (for showing in the preview pane)
  const expandedScene = useMemo(
    () => scenes.find((s) => s.id === expandedSceneId) || null,
    [scenes, expandedSceneId]
  );
  const expandedScenePosition = useMemo(
    () => scenes.findIndex((s) => s.id === expandedSceneId),
    [scenes, expandedSceneId]
  );

  // ---------------------------------------------------------------------------
  // Step indicator
  // ---------------------------------------------------------------------------

  /**
   * SYM-6199. Reaching the preview always rebuilds it first.
   *
   * The "Next: Preview" button asks the server for a fresh composition before
   * it moves, but these step numbers used to move straight there. So a
   * customer who changed a slide's length and then pressed 3 watched the
   * composition as it was before the change -- the timing of the previous
   * version -- while the wizard said nothing. The render was right, which is
   * why it looked like a one-off.
   */
  const goToStepRefreshingPreview = useCallback(
    async (target: WizardStep) => {
      if (target === 3 && onRefreshPreview) {
        await onRefreshPreview(scenes, templateStyle, choices);
      }
      goToStep(target);
    },
    [goToStep, onRefreshPreview, scenes, templateStyle, choices]
  );

  const renderStepIndicator = () => (
    // SYM-6308. Named so a test can tell these controls apart from the two step
    // names in the message under the Preview step's slide list, which say the
    // same words and do the same thing.
    <StepIndicatorBar data-testid="step-row">
      {([1, 2, 3] as WizardStep[]).map((s, i) => {
        const isCompleted = visitedSteps.has(s) && step > s;
        const isActive = step === s;
        const canNavigate =
          !isRendering &&
          !isRefreshingPreview &&
          (visitedSteps.has(s) || s <= step);
        return (
          <React.Fragment key={s}>
            {i > 0 && <StepConnector />}
            <StepPill
              isActive={isActive}
              isCompleted={isCompleted}
              onClick={() => canNavigate && goToStepRefreshingPreview(s)}
              disabled={!canNavigate}
            >
              <StepNumber isActive={isActive} isCompleted={isCompleted}>
                {isCompleted ? <Check size={12} weight="bold" /> : s}
              </StepNumber>
              {STEP_LABELS[s]}
            </StepPill>
          </React.Fragment>
        );
      })}
    </StepIndicatorBar>
  );

  // ---------------------------------------------------------------------------
  // Preview pane (left side) — adapts to current step/state
  // ---------------------------------------------------------------------------

  // Auto-select first scene when entering step 2
  const handleGoToStep2 = useCallback(() => {
    goToStep(2);
    if (!expandedSceneId && scenes.length > 0) {
      setExpandedSceneId(scenes[0].id);
    }
  }, [goToStep, expandedSceneId, scenes]);

  const renderPreviewPane = () => {
    // Preview pane is always visible — gives the user live feedback on all steps
    //
    // SYM-6199. "Live" is not true between an edit and the next rebuild: the
    // composition comes from the server and is only asked for again on the way
    // to the preview step. Rather than let a customer time a slide against a
    // version that predates their change, the pane says which one it is
    // showing (showsAnOlderVersion, above).

    return (
      // SYM-6308. Named so a test can ask which steps draw it: it is now on the
      // Preview step and the render-progress step only, and nowhere else.
      <PreviewPane data-testid="preview-pane">
        <PreviewSlot>
          <PreviewContainer
            aspectRatio="1:1"
            ratio={previewPlan ? planAspect(previewPlan) : undefined}
          >
            {/* Our own renderer's plan: the pictures ffmpeg will composite. */}
            {playsOwnPlan && previewPlan && (
              <Track1PreviewFrame
                plan={previewPlan}
                currentTime={planPlayer.currentTime}
                isPlaying={planPlayer.isPlaying}
              />
            )}

            {/* SDK preview — driven by the BE-provided preview_payload */}
            {!playsOwnPlan && hasPreview && !paneError && (
              <PreviewPlayerEl ref={previewContainerRef} />
            )}

            {/* Placeholder when SDK is unavailable (no public token) or errored */}
            {(!hasPreview || paneError) && (
              <PreviewPlaceholder>
                <VideoCamera size={40} weight="thin" />
                <PreviewPlaceholderText>
                  {paneError
                    ? 'Preview unavailable. Edits will still be sent on Accept.'
                    : 'Preview requires a Creatomate public token.'}
                </PreviewPlaceholderText>
              </PreviewPlaceholder>
            )}
          </PreviewContainer>
        </PreviewSlot>

        {/* Controls — the same ones for either player, hidden while rendering */}
        {hasPreview && !paneError && !isRendering && (
          <PreviewControls>
            <PlayPauseButton
              onClick={() => (playerPlaying ? playerPause() : playerPlay())}
              disabled={!playerReady}
            >
              {playerPlaying ? (
                <Pause size={18} weight="fill" />
              ) : (
                <Play size={18} weight="fill" />
              )}
            </PlayPauseButton>
            <PreviewTime>
              {playerTime.toFixed(1)}s /{' '}
              {/* The plan's own length is what the video is cut to; the sum of
                  the scene lengths ignores the overlap every transition takes
                  out of the slide before it. */}
              {playingLength}s
            </PreviewTime>
          </PreviewControls>
        )}

        {hasPreview && !paneError && !isRendering && showsAnOlderVersion && (
          <StalePreviewNote
            busy={isRefreshingPreview}
            onRetry={() => onRefreshPreview?.(scenes, templateStyle, choices)}
          />
        )}

        {/* Render progress overlay */}
        {isRendering && (
          <div tw="flex-shrink-0 w-full max-w-sm mt-4">
            <p tw="text-sm font-medium text-gray-700 mb-1 text-center">
              Finalizing your video...
            </p>
            <p
              tw="text-xs text-gray-500 mb-2 text-center"
              data-testid="render-wait-note"
            >
              {RENDER_WAIT_NOTE}
            </p>
            <div tw="w-full bg-gray-200 rounded-full h-2">
              <div
                tw="bg-indigo-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${renderProgress}%` }}
              />
            </div>
            <p tw="text-xs text-gray-400 text-center mt-1">
              {Math.round(renderProgress)}%
            </p>
          </div>
        )}
      </PreviewPane>
    );
  };

  // ---------------------------------------------------------------------------
  // Control pane (right side) — changes per step
  // ---------------------------------------------------------------------------

  const renderControlPane = () => {
    switch (step) {
      // Step 1: Style picker — full width, no split layout
      case 1:
        return null; // Rendered directly in the main body, not in ControlPane

      // Step 2: Scene list — rendered directly in main body (not ControlPane)
      case 2:
        return null;

      // Step 3: rendered directly in main body (scenes left, preview right)
      case 3:
        return null;

      // Step 4: Render — control pane shows summary while rendering
      case 4:
        return (
          <ControlPane>
            <h3 tw="text-lg font-semibold text-gray-800 mb-4">
              Rendering your video
            </h3>
            <SummaryCard>
              <SummaryRow>
                <SummaryLabel>Style</SummaryLabel>
                <SummaryValue>
                  {templateStyle.charAt(0).toUpperCase() +
                    templateStyle.slice(1)}
                </SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>Scenes</SummaryLabel>
                <SummaryValue>{scenes.length}</SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>Total duration</SummaryLabel>
                <SummaryValue>{videoLength}s</SummaryValue>
              </SummaryRow>
            </SummaryCard>
            <p tw="text-sm text-gray-500">
              {RENDER_WAIT_NOTE} Your video will be added to your ad variants
              when complete.
            </p>
          </ControlPane>
        );

      default:
        return null;
    }
  };

  // ---------------------------------------------------------------------------
  // Bottom bar — navigation buttons
  // ---------------------------------------------------------------------------

  const renderBottomBar = () => {
    if (isRendering) {
      return (
        <BottomBar>
          <Button variant="outline" size="small" onClick={onCancel}>
            Cancel
          </Button>
          <p tw="text-sm text-gray-500">
            Please wait while your video is being rendered...
          </p>
        </BottomBar>
      );
    }

    const isFirstStep = step === 1;

    return (
      <BottomBar>
        <div tw="flex items-center gap-3">
          {!isFirstStep && (
            <Button
              variant="outline"
              size="small"
              onClick={() => goToStep((step - 1) as WizardStep)}
            >
              <ArrowLeft size={14} tw="mr-1" />
              Back
            </Button>
          )}
          <Button variant="outline" size="small" onClick={onCancel}>
            Cancel
          </Button>
        </div>

        <div tw="flex items-center gap-3">
          {step === 1 && (
            <Button variant="indigo" onClick={() => goToStep(2)}>
              Next: Scenes
              <ArrowRight size={14} tw="ml-1" />
            </Button>
          )}
          {step === 2 && (
            <Button
              variant="indigo"
              disabled={isRefreshingPreview}
              onClick={() => goToStepRefreshingPreview(3)}
            >
              {isRefreshingPreview ? 'Updating preview…' : 'Next: Preview'}
              <ArrowRight size={14} tw="ml-1" />
            </Button>
          )}
          {step === 3 && (
            <Button
              variant="indigo"
              onClick={() => onRender(scenes, templateStyle, choices)}
            >
              <Sparkle size={14} weight="fill" tw="mr-1" />
              Accept &amp; Render
            </Button>
          )}
        </div>
      </BottomBar>
    );
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // Step 1: Style picker, the whole width
  //
  // SYM-6308. The playing video used to take 60% of this step. Choosing a look
  // is not watching the video you already have, and the examples on the style
  // cards (SYM-6226) show what each look does. So the picker has the pop-up to
  // itself and the video waits on the Preview step.
  if (step === 1) {
    return (
      <Wrapper>
        {renderStepIndicator()}
        <SplitLayout>
          <StylePane>
            <StylePickerHeading>Choose a video style</StylePickerHeading>
            <StylePickerSubtext>
              Pick a visual style for your recruitment video.
            </StylePickerSubtext>
            <TemplateStylePicker
              selected={templateStyle}
              onSelect={handleStyleSelect}
              examples={styleExamples}
              onRetryExamples={onRetryStyleExamples}
            />
          </StylePane>
        </SplitLayout>
        {renderBottomBar()}
      </Wrapper>
    );
  }

  // Step 2: Scene list LEFT (35%), scene editor RIGHT (65%)
  //
  // SYM-6308. The playing video was a third pane here, and the three of them
  // asked for 160% of the pop-up, so every one of them was squeezed. Without it
  // the two that are being used get the width they ask for.
  if (step === 2) {
    return (
      <Wrapper>
        {renderStepIndicator()}
        <SplitLayout>
          <SceneListPane>
            <div tw="flex items-center justify-between mb-3">
              <SectionHeading>Scenes</SectionHeading>
              {canAddSlide(scenes.length) ? (
                <Button variant="outline" size="small" onClick={handleAddScene}>
                  <Plus size={14} tw="mr-1" />
                  Add
                </Button>
              ) : (
                // SYM-6194. Past this many the server keeps only the first few
                // and says nothing, so the customer is stopped here instead.
                <span tw="text-xs text-gray-500">
                  {MAX_SLIDES} scenes is the most a video holds
                </span>
              )}
            </div>

            {/*
              SYM-6265. One switch for the whole tab. When something that
              belongs to the whole video is not as the look leaves it, the
              switch says so even while it is off.
            */}
            <div tw="flex items-center justify-between mb-3">
              <AdvancedSwitch
                type="button"
                aria-pressed={advanced}
                onClick={() => setAdvanced((on) => !on)}
              >
                <SlidersHorizontal size={14} />
                {advanced ? 'Hide advanced' : 'Advanced'}
                {!advanced &&
                  (logoIsSetByHand(logoPosition) ||
                    scenes.some(sceneIsSetByHand)) && <RowHandDot />}
              </AdvancedSwitch>
              {!advanced && logoIsSetByHand(logoPosition) && (
                <HandSetMarker
                  type="button"
                  tw="mt-0"
                  data-testid="logo-hand-marker"
                  onClick={() => setAdvanced(true)}
                >
                  {logoMarker(logoPosition)}
                </HandSetMarker>
              )}
            </div>

            {/*
              SYM-6353. The four places of the shared logo row (the image-ad
              row and Bring to Life use the same one), top middle included.
            */}
            {advanced && (
              <AdvancedPanel data-testid="advanced-logo-row">
                <FieldLabel>Logo, on every slide</FieldLabel>
                <div tw="flex flex-wrap gap-2">
                  <LogoPlaceButtons
                    value={logoPosition}
                    onPick={setLogoPosition}
                  />
                </div>
              </AdvancedPanel>
            )}

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="wizard-scenes-step2" direction="vertical">
                {(provided) => (
                  <SceneListContainer
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {scenes.map((scene, index) => {
                      const isSelected = expandedSceneId === scene.id;
                      return (
                        <Draggable
                          key={scene.id}
                          draggableId={scene.id}
                          index={index}
                        >
                          {(dragProvided, dragSnapshot) => (
                            <SceneRow
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              isExpanded={isSelected}
                              isDragging={dragSnapshot.isDragging}
                            >
                              <SceneRowHeader
                                onClick={() => {
                                  setExpandedSceneId(scene.id);
                                }}
                              >
                                <DragHandle
                                  {...dragProvided.dragHandleProps}
                                  onClick={(e: React.MouseEvent) =>
                                    e.stopPropagation()
                                  }
                                >
                                  <DotsSixVertical size={16} weight="bold" />
                                </DragHandle>

                                <SceneThumb
                                  $ratio={cropAspect}
                                  src={
                                    scene.croppedPreviewUrl || scene.imageUrl
                                  }
                                  alt={scene.headline}
                                  loading="lazy"
                                />

                                <SceneInfo>
                                  <SceneHeadline>
                                    {scene.headline}
                                  </SceneHeadline>
                                  {/*
                                    SYM-6201. The row says what the slide does,
                                    so the first one does not claim a change it
                                    never makes: the server discards the first
                                    slide's transition, and the editor already
                                    stops offering it there. The setting is kept
                                    on the slide rather than cleared, so moving
                                    that slide down the list brings its old
                                    choice back with it.
                                  */}
                                  {/*
                                    SYM-6265. Length and transition are the
                                    look's business, so the row only names
                                    them under Advanced. A slide whose values
                                    were set by hand carries a dot either way.
                                  */}
                                  {(advanced || sceneIsSetByHand(scene)) && (
                                    <SceneMeta>
                                      <Clock size={12} />
                                      {advanced
                                        ? sceneRowMeta(
                                            {
                                              duration: scene.duration,
                                              transition: shownTransition(
                                                scene,
                                                templateStyle,
                                                index,
                                                stylePaces
                                              ),
                                            },
                                            index
                                          )
                                        : handSetNote(scene)}
                                      {sceneIsSetByHand(scene) && (
                                        <RowHandDot />
                                      )}
                                    </SceneMeta>
                                  )}
                                </SceneInfo>

                                <SceneActions>
                                  {scenes.length > 1 && (
                                    <TrashButton
                                      onClick={(e: React.MouseEvent) => {
                                        e.stopPropagation();
                                        handleRemoveScene(scene.id);
                                      }}
                                    >
                                      <Trash size={14} />
                                    </TrashButton>
                                  )}
                                </SceneActions>
                              </SceneRowHeader>
                            </SceneRow>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </SceneListContainer>
                )}
              </Droppable>
            </DragDropContext>
          </SceneListPane>

          <SceneEditorPane>
            {expandedScene ? (
              <>
                <SectionHeading tw="mb-3">
                  Edit Scene {expandedScenePosition + 1}
                </SectionHeading>
                <InlineSceneEditor
                  key={expandedScene.id}
                  scene={expandedScene}
                  onUpdate={handleUpdateScene}
                  vacancyId={vacancyId}
                  cropAspect={cropAspect}
                  isFirstSlide={expandedScenePosition === 0}
                  advanced={advanced}
                  transitionShown={shownTransition(
                    expandedScene,
                    templateStyle,
                    expandedScenePosition,
                    stylePaces
                  )}
                  handNote={handSetNote(expandedScene)}
                  onOpenAdvanced={() => setAdvanced(true)}
                  onBackToLook={() =>
                    setScenes((prev) =>
                      prev.map((sc, i) =>
                        sc.id === expandedScene.id
                          ? backToLook(sc, templateStyle, i, stylePaces)
                          : sc
                      )
                    )
                  }
                  characterBudget={characterBudget}
                />
              </>
            ) : (
              <div tw="flex flex-col items-center justify-center h-full text-gray-400">
                <p tw="text-sm">Select a scene to edit</p>
              </div>
            )}
          </SceneEditorPane>
        </SplitLayout>
        {renderBottomBar()}

        {/* SYM-6194. The picture for a slide being added. */}
        <ResourcesModal
          show={showAddPicker}
          mode="image"
          onClose={() => setShowAddPicker(false)}
          onSubmit={handleAddScenePicked}
        />
      </Wrapper>
    );
  }

  // Step 3: Scenes LEFT (same as Step 2 position) + Video preview RIGHT
  //
  // SYM-6308. This is where the wizard opens and the only step that shows the
  // video, so the left pane has to say where the changing is done.
  if (step === 3) {
    return (
      <Wrapper>
        {renderStepIndicator()}
        <SplitLayout>
          <ReviewListPane>
            <SectionHeading tw="mb-3">Review your video</SectionHeading>
            <SummaryCard tw="mb-4">
              <SummaryRow>
                <SummaryLabel>Style</SummaryLabel>
                <SummaryValue>
                  {templateStyle.charAt(0).toUpperCase() +
                    templateStyle.slice(1)}
                </SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>Scenes</SummaryLabel>
                <SummaryValue>{scenes.length}</SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>Duration</SummaryLabel>
                <SummaryValue>{videoLength}s</SummaryValue>
              </SummaryRow>
            </SummaryCard>

            <div tw="space-y-2">
              {scenes.map((scene, i) => (
                <div
                  key={scene.id}
                  tw="flex items-center gap-3 px-3 py-2 bg-white rounded-lg border border-gray-200"
                >
                  <span tw="text-xs font-bold text-gray-400 w-4">{i + 1}</span>
                  <SceneThumb
                    $ratio={cropAspect}
                    src={scene.croppedPreviewUrl || scene.imageUrl}
                    alt={scene.headline}
                  />
                  <div tw="flex-1 min-w-0">
                    <p tw="text-sm font-medium text-gray-700 truncate">
                      {scene.headline}
                    </p>
                  </div>
                  <span tw="text-xs text-gray-400">{scene.duration}s</span>
                </div>
              ))}
            </div>

            {/*
              SYM-6308. The two step names are the controls, and they go
              through the very handler the numbered step row uses
              (goToStepRefreshingPreview), so a change to what moving between
              steps means is picked up here without anybody remembering to.
              Neither of these two targets triggers the rebuild inside it; only
              arriving at Preview does.
            */}
            <WhereToChange data-testid="where-to-change">
              To change the pictures or the words, go to{' '}
              <StepLink
                type="button"
                onClick={() => goToStepRefreshingPreview(2)}
              >
                {STEP_LABELS[2]}
              </StepLink>
              . To change the look, go to{' '}
              <StepLink
                type="button"
                onClick={() => goToStepRefreshingPreview(1)}
              >
                {STEP_LABELS[1]}
              </StepLink>
              . Your changes show up here.
            </WhereToChange>
          </ReviewListPane>

          {renderPreviewPane()}
        </SplitLayout>
        {renderBottomBar()}
      </Wrapper>
    );
  }

  // Step 4: Render progress (full width)
  return (
    <Wrapper>
      {renderStepIndicator()}
      <SplitLayout>
        {renderPreviewPane()}
        {renderControlPane()}
      </SplitLayout>
      {renderBottomBar()}
    </Wrapper>
  );
};

export default VideoWizard;
